import { useMemo, useState, type ReactNode } from "react";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  type PublicClientApplication,
} from "@azure/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { AuthContext, type AuthContextValue } from "./AuthContext";
import { API_SCOPES, LOGIN_SCOPES, createMsalInstance } from "./msalConfig";

// Autenticação de produção: login por redirect no Microsoft Entra External ID.
// Nenhum formulário próprio captura credenciais; a tela de login é do Entra.
export function ExternalIdAuthProvider({ children }: { children: ReactNode }) {
  const [instance] = useState<PublicClientApplication>(createMsalInstance);
  return (
    <MsalProvider instance={instance}>
      <ExternalIdBridge>{children}</ExternalIdBridge>
    </MsalProvider>
  );
}

function ExternalIdBridge({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const authenticated = useIsAuthenticated();
  const [error, setError] = useState<string | null>(null);

  const value = useMemo<AuthContextValue>(() => {
    const account = accounts[0];
    const busy = inProgress !== InteractionStatus.None;
    return {
      mode: "external_id",
      status: busy ? "loading" : authenticated && account ? "signed_in" : "signed_out",
      user: account ? { name: account.name || account.username, email: account.username } : null,
      error,
      signIn: () => {
        setError(null);
        instance.loginRedirect({ scopes: LOGIN_SCOPES }).catch((cause: unknown) => {
          const detail = cause instanceof Error ? cause.message : "erro desconhecido";
          setError(`Não foi possível iniciar o login: ${detail}`);
        });
      },
      signOut: () => {
        instance.logoutRedirect({ account }).catch((cause: unknown) => {
          const detail = cause instanceof Error ? cause.message : "erro desconhecido";
          setError(`Não foi possível encerrar a sessão: ${detail}`);
        });
      },
      getAccessToken: async () => {
        if (!account) throw new Error("Sessão não iniciada.");
        try {
          const result = await instance.acquireTokenSilent({ scopes: API_SCOPES, account });
          return result.accessToken;
        } catch (cause) {
          if (cause instanceof InteractionRequiredAuthError) {
            await instance.acquireTokenRedirect({ scopes: API_SCOPES, account });
          }
          throw cause;
        }
      },
    };
  }, [instance, accounts, inProgress, authenticated, error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
